import React, { CSSProperties, FC, FocusEventHandler, ReactNode, useCallback, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { NumericFormat, NumericFormatProps, NumberFormatValues, PatternFormat, SourceInfo } from 'react-number-format';
import { useUncontrolledInput } from 'modules/core/hooks';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { IFormField } from 'modules/core/types/form';
import { ASTERISK, EMPTY_STRING, SLASH_SYMBOL } from 'modules/core/constants';

import { Adornment, Input as InputUI, InputFontWeight } from './styles';
import { TUnderlyingHTMLTextElement } from './types';
import { Spiner } from '../Spiner/Spiner';

const { Control: FormControl } = Form;

/**
 * Whether an `onValueChange` is react-number-format re-emitting its own `decimalScale` rounding of
 * a value we set through props, rather than the user typing. The library reports `'event'` for a
 * real edit and `'prop'` for this echo, and exports `SourceType` only as a type, not as a value.
 */
const isFormatterEcho = (sourceInfo?: SourceInfo): boolean => sourceInfo?.source === 'prop';

type ChangeNumberInput = Pick<ControllerRenderProps, 'onChange'>['onChange'];

type InputNumberProps = Pick<NumericFormatProps, 'thousandSeparator' | 'decimalScale'>;

export interface IInputAdornment {
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  endAdornmentOnClick?: Function;
}

export interface IInput extends IFormField, IInputAdornment, InputNumberProps {
  placeholder?: string;
  textPosition?: 'end' | 'start';
  textStyle?: InputFontWeight;
  valueAsNumber?: boolean;
  disabled?: boolean;
  format?: string;
  mask?: string;
  allowEmptyFormatting?: boolean;
  allowNegative?: boolean;
  type?: string;
  as?: TUnderlyingHTMLTextElement;
  changeCallback?: (event?: any) => void;
  clickCallback?: VoidFunction;
  blurCallback?: FocusEventHandler<HTMLInputElement> | undefined;
  testId?: string;
  required?: boolean;
  readonly?: boolean;
  autoComplete?: boolean;
  hideInitZeroValue?: boolean;
  isInvalid?: boolean;
  maxLength?: number;
  className?: string;
  isAllowTypeSpace?: boolean;
  placeholderStyles?: CSSProperties | undefined;
  isComponentAnimated?: boolean;
  isSpinerAnimated?: boolean;
  placeholderWithoutAsterisk?: boolean;
  handleEnterKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  shouldValidateZeroEntered?: boolean;
  /**
   * Keep the form value exactly as it was set, even when `decimalScale` displays fewer digits.
   * Use for fields fed a derived, high-precision number (e.g. a deal source's Max LTV) where
   * rounding the stored value to the display scale would change what the calculation receives.
   */
  preserveIncomingPrecision?: boolean;
}

export const Input: FC<IInput> = ({
  name,
  rules = {},
  startAdornment: StartAdornment,
  endAdornment: EndAdornment,
  endAdornmentOnClick,
  placeholder,
  validate,
  valueAsNumber = true,
  textPosition = 'start',
  textStyle = 'normal',
  disabled = false,
  thousandSeparator = true,
  format,
  mask,
  allowEmptyFormatting = false,
  allowNegative = false,
  type = 'text',
  as = 'input',
  maxLength = undefined,
  defaultValue,
  changeCallback,
  clickCallback,
  blurCallback,
  decimalScale,
  testId,
  required,
  readonly,
  autoComplete = false,
  hideInitZeroValue = false,
  isInvalid = false,
  isAllowTypeSpace = true,
  className,
  placeholderStyles = undefined,
  isComponentAnimated = false,
  isSpinerAnimated = false,
  placeholderWithoutAsterisk,
  handleEnterKeyDown,
  shouldValidateZeroEntered = true,
  preserveIncomingPrecision = false,
}) => {
  const { hasError, errorMessage, controlField } = useUncontrolledInput(name);
  const [isZeroEntered, setIsZeroEntered] = useState(false);
  const isZeroManuallyEntered = shouldValidateZeroEntered && isZeroEntered;

  const processedPlaceholder = required && !placeholderWithoutAsterisk ? `${placeholder}${ASTERISK}` : placeholder;

  const commonInputProps = useMemo(
    () => ({
      placeholder: processedPlaceholder,
      name,
      isInvalid: isInvalid ? isInvalid : Boolean(errorMessage) || hasError,
      disabled,
    }),
    [disabled, errorMessage, hasError, isInvalid, name, processedPlaceholder],
  );

  const renderAdornment = useCallback(
    (AdornmentComponent: ReactNode, adornmentOnClick?: Function, isCursorPointer?: boolean) => (
      <Adornment $isCursorPointer={isCursorPointer} onClick={adornmentOnClick}>
        {AdornmentComponent}
      </Adornment>
    ),
    [],
  );

  const handleNumberInputChange = useCallback(
    (onChange: ChangeNumberInput) => ({ value }: NumberFormatValues, sourceInfo?: SourceInfo) => {
      // Storing the echo would round the form value down to the display scale.
      if (preserveIncomingPrecision && isFormatterEcho(sourceInfo)) return;

      onChange(hideInitZeroValue && value === EMPTY_STRING ? 0 : value);
      setIsZeroEntered(value === '0');
    },
    [hideInitZeroValue, preserveIncomingPrecision],
  );

  const handleSpaceKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    // Check if the key pressed is space (key code 32)
    if (event.keyCode === 32) {
      event.preventDefault();
    }
  }, []);

  const renderNumberInput = useCallback(
    ({ onChange, onBlur, value, ref }: ControllerRenderProps) =>
      format || mask ? (
        <PatternFormat
          {...commonInputProps}
          autoComplete={autoComplete ? 'on' : 'off'}
          customInput={FormControl as React.ComponentType<any>}
          onValueChange={handleNumberInputChange(onChange)}
          onChange={changeCallback}
          onClick={clickCallback}
          onBlur={blurCallback || onBlur}
          value={value === 0 && hideInitZeroValue && !isZeroManuallyEntered ? EMPTY_STRING : value}
          format={format!}
          mask={mask}
          allowEmptyFormatting={allowEmptyFormatting}
          placeholder={placeholder}
          readOnly={readonly}
          maxLength={maxLength}
          getInputRef={ref}
        />
      ) : (
        <NumericFormat
          {...commonInputProps}
          autoComplete={autoComplete ? 'on' : 'off'}
          customInput={FormControl as React.ComponentType<any>}
          thousandSeparator={thousandSeparator}
          onValueChange={handleNumberInputChange(onChange)}
          onChange={changeCallback}
          onClick={clickCallback}
          onBlur={blurCallback || onBlur}
          prefix={EMPTY_STRING}
          value={value === 0 && hideInitZeroValue && !isZeroManuallyEntered ? EMPTY_STRING : value}
          allowNegative={allowNegative}
          decimalScale={decimalScale}
          placeholder={placeholder}
          readOnly={readonly}
          maxLength={maxLength}
        />
      ),
    [
      commonInputProps,
      autoComplete,
      thousandSeparator,
      handleNumberInputChange,
      changeCallback,
      clickCallback,
      blurCallback,
      hideInitZeroValue,
      format,
      mask,
      allowEmptyFormatting,
      allowNegative,
      decimalScale,
      placeholder,
      readonly,
      maxLength,
      isZeroManuallyEntered,
    ],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isAllowTypeSpace) {
        handleSpaceKeyDown(event);
      }

      handleEnterKeyDown?.(event);
    },
    [handleEnterKeyDown, handleSpaceKeyDown, isAllowTypeSpace],
  );

  const renderTextField = useCallback(
    ({ onChange, onBlur, ref, value }) => (
      <FormControl
        {...commonInputProps}
        autoComplete={autoComplete ? 'on' : 'off'}
        ref={ref}
        type={type}
        as={as}
        onChange={(e) => {
          onChange(e);
          changeCallback && changeCallback(e);
        }}
        maxLength={maxLength}
        onKeyDown={handleKeyDown}
        onBlur={blurCallback || onBlur}
        value={value}
      />
    ),
    [commonInputProps, autoComplete, type, as, maxLength, handleKeyDown, changeCallback, blurCallback],
  );

  const parsedErrorMessage = useMemo(
    () => errorMessage?.split(SLASH_SYMBOL)?.map((error: string) => error && <div key={error}>{error}</div>),
    [errorMessage],
  );

  return (
    <>
      <InputUI
        className={className}
        textposition={textPosition}
        textstyle={textStyle}
        data-testid={testId}
        data-tooltip-id={name}
        $isInvalid={commonInputProps.isInvalid}
        $endAdornment={Boolean(EndAdornment)}
        $startAdornment={Boolean(StartAdornment)}
        $isClickCallback={clickCallback}
        $readonly={readonly}
        $placeholderStyles={placeholderStyles}
        $isAnimated={isComponentAnimated}
        $isDisabled={disabled}
      >
        {StartAdornment && renderAdornment(StartAdornment)}
        <Controller
          render={valueAsNumber ? renderNumberInput : renderTextField}
          defaultValue={defaultValue || EMPTY_STRING}
          control={controlField}
          name={name}
          rules={{
            ...rules,
            validate,
            valueAsNumber,
          }}
        />
        <Spiner style={{ display: isSpinerAnimated ? 'block' : 'none', left: !StartAdornment ? '10px' : '35px' }} />
        {EndAdornment && renderAdornment(EndAdornment, endAdornmentOnClick, !!endAdornmentOnClick)}
      </InputUI>
      <FormControl.Feedback
        type="invalid"
        className={`${commonInputProps.isInvalid && errorMessage && errorMessage.length ? 'd-block' : 'd-none'}`}
      >
        {parsedErrorMessage}
      </FormControl.Feedback>
    </>
  );
};
